// src/components/ui/sections/CommunicationsTemplateEditor/CommunicationsTemplateEditorShadcn.jsx
import React, { useState, useEffect } from 'react';
import {
  Mail,
  MessageSquare,
  Settings,
  Play,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';

// shadcn components
import { Card, CardContent, CardHeader } from '../../card';
import { Button } from '../../button';

// Import our custom shadcn components
import CommunicationsTemplateDetailsFormShadcn from '../../forms/CommunicationsTemplateDetailsForm/CommunicationsTemplateDetailsFormShadcn';

import { cn } from '../../../../utils/cn';

const CommunicationsTemplateEditorShadcn = ({
  template,
  templateType,
  isCreating = false,
  onCreateTemplate,
  onTriggerChange,
  onUpdateTemplate,
  onDeleteTemplate,
  onTestTemplate,
  savingTemplate = false,
  canEditTemplates = true
}) => {
  const [activeTab, setActiveTab] = useState('details');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentTriggerKey, setCurrentTriggerKey] = useState(template?.template_key || '');

  // Reset tab when template changes
  useEffect(() => {
    setActiveTab('details');
    setHasUnsavedChanges(false);
    setCurrentTriggerKey(template?.template_key || '');
  }, [template?.id]);

  // Handle active toggle from header
  const handleActiveToggle = async () => {
    try {
      await onUpdateTemplate(template.id, {
        name: template.name,
        template_key: template.template_key,
        active: !template.active,
        ...(templateType === 'email' ? {
          subject: template.subject,
          body_html: template.body_html,
          body_text: template.body_text
        } : {
          message_body: template.message_body
        })
      });
    } catch (error) {
      console.error('Failed to update template status:', error);
    }
  };

  // Empty state when no template selected
  if (!template) {
    return (
      <Card className="h-full flex flex-col">
        {/* Header matching the filled state */}
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className={cn(
                "w-8 h-8 rounded-lg flex items-center justify-center",
                templateType === 'email'
                  ? "bg-blue-500/20 text-blue-400"
                  : "bg-green-500/20 text-green-400"
              )}>
                {templateType === 'email' ? (
                  <Mail className="w-4 h-4" />
                ) : (
                  <MessageSquare className="w-4 h-4" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold text-foreground truncate">
                  Select a Template
                </h2>
              </div>
            </div>
          </div>
        </CardHeader>

        {/* Content area matching the filled state */}
        <CardContent className="flex-1 min-h-0 overflow-y-auto flex items-center justify-center">
          <div className="text-center">
            <p className="text-muted-foreground text-sm max-w-sm">
              Choose a template from the list to view and edit its content.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      {/* Enhanced Header */}
      <CardHeader className="flex-shrink-0">
        {/* Template Info Row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className={cn(
              "w-8 h-8 rounded-lg flex items-center justify-center",
              templateType === 'email'
                ? "bg-blue-500/20 text-blue-400"
                : "bg-green-500/20 text-green-400"
            )}>
              {templateType === 'email' ? (
                <Mail className="w-4 h-4" />
              ) : (
                <MessageSquare className="w-4 h-4" />
              )}
            </div>

            <div className="min-w-0 flex-1">
              <h2 className="font-semibold text-foreground truncate">
                {template.name}
              </h2>
            </div>
          </div>

          {/* Right side controls */}
          <div className="flex items-center gap-3">
            {/* Active Toggle */}
            <Button
              type="button"
              variant={template.active ? "default" : "secondary"}
              size="sm"
              onClick={handleActiveToggle}
              disabled={savingTemplate || template?.isTemporary}
              className={cn(
                "flex items-center gap-2",
                template.active
                  ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/30"
                  : ""
              )}
            >
              {template.active ? (
                <>
                  <ToggleRight className="w-4 h-4" />
                  Active
                </>
              ) : (
                <>
                  <ToggleLeft className="w-4 h-4" />
                  Inactive
                </>
              )}
            </Button>

            {/* Test Button */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onTestTemplate && onTestTemplate(template.id)}
              disabled={savingTemplate || template?.isTemporary}
              className="flex-shrink-0"
            >
              <Play className="mr-2 h-4 w-4" />
              Test
            </Button>
          </div>
        </div>
      </CardHeader>

      {/* Tab Content */}
      <CardContent className="flex-1 min-h-0 overflow-y-auto p-0">
        <CommunicationsTemplateDetailsFormShadcn
          template={template}
          templateType={templateType}
          isCreating={isCreating}
          onCreateTemplate={onCreateTemplate}
          onTriggerChange={onTriggerChange}
          onUpdateTemplate={onUpdateTemplate}
          onDeleteTemplate={onDeleteTemplate}
          canEditTemplates={canEditTemplates}
          savingTemplate={savingTemplate}
          onChangesSaved={() => setHasUnsavedChanges(false)}
          onChangesDetected={() => setHasUnsavedChanges(true)}
          onTriggerKeyChange={setCurrentTriggerKey}
        />
      </CardContent>
    </Card>
  );
};

export default CommunicationsTemplateEditorShadcn;